import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Phone, Mail, MapPin, Trash2, ChevronDown, Globe, Search, Pencil } from "lucide-react";
import { toast } from "sonner";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { ClientPortalDialog } from "@/components/ClientPortalDialog";

export default function Clients() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const shownItems = items.filter((c) => !ql ||
    [c.name, c.contact_person, c.email, c.phone, c.address]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(ql)));
  const [portalClient, setPortalClient] = useState(null);
  const [editClient, setEditClient] = useState(null);

  const load = async () => {
    const { data } = await api.get("/clients");
    setItems(data);
    // Deep link from global search: /app/clients?id=... expands that client.
    const deepId = new URLSearchParams(window.location.search).get("id");
    if (deepId && data.some((c) => c.id === deepId)) {
      setExpanded((s) => ({ ...s, [deepId]: true }));
      setTimeout(() => document.querySelector(`[data-testid="client-card-${deepId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Client removed");
    load();
  };

  return (
    <div data-testid="clients-page" className="p-6 lg:p-10 max-w-7xl mx-auto">
      <PageHeader
        tag="/ Customers"
        title="Clients"
        subtitle="Your client database. Connect them to jobs to keep history tidy."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="new-client-button">
                <Plus className="w-4 h-4 mr-2" /> New client
              </Button>
            </DialogTrigger>
            <ClientForm onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      />

      {items.length > 0 && (
        <div className="relative max-w-sm mb-4">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients, contacts, emails…"
                 className="pl-9 w-full rounded-lg border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620]"
                 data-testid="client-search-input" />
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="No clients yet" description="Add your first client to start linking jobs." />
      ) : shownItems.length === 0 ? (
        <EmptyState title="No matches" description="Try a different search." />
      ) : (
        <div className="space-y-2">
          {shownItems.map((c) => {
            const isOpen = !!expanded[c.id];
            return (
            <div key={c.id} className="hard-card" data-testid={`client-card-${c.id}`}>
              <button type="button" onClick={() => setExpanded((s) => ({ ...s, [c.id]: !s[c.id] }))} className="w-full text-left px-4 py-3 hover:bg-zinc-50 dark:hover:bg-white/[0.03] transition-colors" data-testid={`client-toggle-${c.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex items-center gap-3 flex-wrap">
                    <h3 className="font-display font-bold uppercase tracking-tight text-[15px]">{c.name}</h3>
                    {c.contact_person && <span className="text-sm text-zinc-500">{c.contact_person}</span>}
                    {!isOpen && (
                      <span className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
                        {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                        {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {isOpen && (
                <div className="px-5 pb-4 border-t border-zinc-200 pt-4 space-y-3" data-testid={`client-detail-${c.id}`}>
                  <div className="space-y-2 text-sm text-zinc-700">
                    {c.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-zinc-400" />{c.phone}</div>}
                    {c.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-zinc-400" />{c.email}</div>}
                    {c.address && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-zinc-400 mt-0.5" />{c.address}</div>}
                  </div>
                  {c.notes && (
                    <div className="pt-2 border-t border-zinc-100">
                      <div className="label-tag mb-1">Notes</div>
                      <p className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{c.notes}</p>
                    </div>
                  )}
                  <div className="text-[10px] text-zinc-400 font-mono uppercase tracking-widest pt-2">
                    Added {new Date(c.created_at).toLocaleDateString("en-NZ")}
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-zinc-100">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="rounded-sm uppercase tracking-wider font-bold text-xs" onClick={() => setPortalClient(c)} data-testid={`portal-client-${c.id}`}>
                        <Globe className="w-3 h-3 mr-1" /> Portal
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-sm uppercase tracking-wider font-bold text-xs" onClick={() => setEditClient(c)} data-testid={`edit-client-${c.id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                    </div>
                    <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-600 rounded-sm uppercase tracking-wider font-bold text-xs" onClick={() => remove(c.id)} data-testid={`delete-client-${c.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
      {editClient && (
        <Dialog open={!!editClient} onOpenChange={(o) => { if (!o) setEditClient(null); }}>
          <ClientForm client={editClient} onDone={() => { setEditClient(null); load(); }} />
        </Dialog>
      )}
      {portalClient && (
        <ClientPortalDialog
          clientId={portalClient.id}
          clientName={portalClient.name}
          open={!!portalClient}
          onOpenChange={(o) => { if (!o) setPortalClient(null); }}
        />
      )}
    </div>
  );
}

function ClientForm({ onDone, client }) {
  const [form, setForm] = useState({
    name: client?.name || "", contact_person: client?.contact_person || "", email: client?.email || "",
    phone: client?.phone || "", address: client?.address || "", notes: client?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (client) {
        await api.patch(`/clients/${client.id}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/clients", form);
        toast.success("Client added");
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <DialogContent className="rounded-sm">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight text-2xl">{client ? "Edit client" : "New client"}</DialogTitle>
        <DialogDescription>{client ? "Update this customer's details." : "Add a customer record. Jobs can be linked to clients for tidy history."}</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="label-tag">Company name</Label>
          <Input required value={form.name} onChange={set("name")} className="mt-2 rounded-sm border-zinc-400" data-testid="client-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Contact</Label>
            <Input value={form.contact_person} onChange={set("contact_person")} className="mt-2 rounded-sm border-zinc-400" data-testid="client-contact-input" />
          </div>
          <div>
            <Label className="label-tag">Phone</Label>
            <Input value={form.phone} onChange={set("phone")} className="mt-2 rounded-sm border-zinc-400" data-testid="client-phone-input" />
          </div>
        </div>
        <div>
          <Label className="label-tag">Email</Label>
          <Input type="email" value={form.email} onChange={set("email")} className="mt-2 rounded-sm border-zinc-400" data-testid="client-email-input" />
        </div>
        <div>
          <Label className="label-tag">Address</Label>
          <div className="mt-2">
            <AddressAutocomplete
              value={form.address}
              onChange={(v) => setForm((f) => ({ ...f, address: v }))}
              placeholder="Start typing an NZ address…"
              className="rounded-sm border-zinc-400"
              testid="client-address-input"
            />
          </div>
        </div>
        <div>
          <Label className="label-tag">Notes</Label>
          <Textarea rows={3} value={form.notes} onChange={set("notes")} className="mt-2 rounded-sm border-zinc-400" data-testid="client-notes-input" />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="client-submit-button">
            {busy ? "Saving..." : client ? "Save changes" : "Add client"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
